import { useMutation } from 'react-query';
import * as RestApi from 'core/network/RestApi';
import useNotifications from 'core/notifications/useNotifications';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { OnsiteCampaignResponse } from '../types';

/**
 * Returns a mutate function that takes a campaign and either creates it
 * or updates it based on whether the "id" proprty is present (upsert).
 */
export function useUpsertCampaignMutation(siteId: string) {
  const history = useHistory();
  const { t } = useTranslation('campaigns');
  const { showNotification } = useNotifications();

  const updateCampaignMutation = useMutation((campaign: OnsiteCampaignResponse) =>
    RestApi.put<OnsiteCampaignResponse, OnsiteCampaignResponse>(
      `/sites/${siteId}/onsite/campaigns/${campaign.id}`,
      campaign,
    ),
  );
  const createCampaignMutation = useMutation((campaign: OnsiteCampaignResponse) =>
    RestApi.post<OnsiteCampaignResponse, OnsiteCampaignResponse>(`/sites/${siteId}/onsite/campaigns`, campaign),
  );

  function mutate(selectedCampaign: OnsiteCampaignResponse, onSuccessCallback?: () => void) {
    if (selectedCampaign.id === 'new') {
      createCampaignMutation.mutate(selectedCampaign, {
        onSuccess: response => {
          showNotification(t('onsite.createCampaignNotification.success'));
          history.push(`/sites/${siteId}/campaigns/onsite/${response.data.id}/workflow`);
          if (onSuccessCallback) {
            onSuccessCallback();
          }
        },
        onError: (error: RestApi.RestApiError) => {
          const errorMessage = RestApi.getRestApiErrorMessage(error);
          showNotification(t('onsite.createCampaignNotification.error'), 'error', undefined, errorMessage || undefined);
        },
      });
    } else {
      updateCampaignMutation.mutate(selectedCampaign, {
        onSuccess: () => {
          showNotification(t('onsite.updateCampaignNotification.success'));
          if (onSuccessCallback) {
            onSuccessCallback();
          }
        },
        onError: (error: RestApi.RestApiError) => {
          const errorMessage = RestApi.getRestApiErrorMessage(error);
          showNotification(t('onsite.updateCampaignNotification.error'), 'error', undefined, errorMessage || undefined);
        },
      });
    }
  }
  return {
    mutate,
  };
}
